import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CreateStateRequest,
  GetStatesParams,
  UpdateStateRequest,
} from "../api/services/state/type";
import { stateService } from "../api/services/state/stateService";

const STATES_STALE_TIME =
  Number(process.env.REACT_APP_STATES_STALE_TIME) || 5 * 60 * 1000;

export const stateKeys = {
  all: ["states"] as const,
  lists: () => [...stateKeys.all, "list"] as const,
  list: (params: GetStatesParams) => [...stateKeys.lists(), params] as const,
  details: () => [...stateKeys.all, "detail"] as const,
  detail: (id: string) => [...stateKeys.details(), id] as const,
  byCountry: (countryId: string) =>
    [...stateKeys.all, "country", countryId] as const,
};

export const useStates = (params: GetStatesParams = {}) => {
  return useQuery({
    queryKey: stateKeys.list(params),
    queryFn: () => stateService.getStates(params),
    staleTime: STATES_STALE_TIME,
  });
};

export const useStatesByCountry = (countryId: string) => {
  return useQuery({
    queryKey: stateKeys.byCountry(countryId),
    queryFn: () => stateService.getStatesByCountry(countryId),
    enabled: !!countryId,
    staleTime: STATES_STALE_TIME,
  });
};

export const useIndividualState = (id: string) => {
  return useQuery({
    queryKey: stateKeys.detail(id),
    queryFn: () => stateService.getState(id),
    enabled: !!id,
    staleTime: STATES_STALE_TIME,
  });
};

export const useCreateState = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateStateRequest) => stateService.createState(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stateKeys.lists() });
      queryClient.invalidateQueries({ queryKey: stateKeys.all });
    },
  });
};

export const useUpdateState = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      stateId,
      data,
    }: {
      stateId: string;
      data: UpdateStateRequest;
    }) => stateService.updateState(stateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: stateKeys.all });
    },
  });
};

export const useDeleteState = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => stateService.deleteState(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({ queryKey: stateKeys.detail(deletedId) });
      queryClient.invalidateQueries({ queryKey: stateKeys.lists() });
    },
  });
};

export const useStateManagement = (initialParams: GetStatesParams = {}) => {
  const queryClient = useQueryClient();

  const statesQuery = useStates(initialParams);
  const createMutation = useCreateState();
  const updateMutation = useUpdateState();
  const deleteMutation = useDeleteState();

  const refreshStates = () => {
    queryClient.invalidateQueries({ queryKey: stateKeys.lists() });
  };

  const clearCache = () => {
    queryClient.clear();
  };

  return {
    states: statesQuery.data?.states || [],
    isLoading: statesQuery.isLoading,
    isError: statesQuery.isError,
    error: statesQuery.error,

    createState: createMutation.mutate,
    updateState: updateMutation.mutate,
    deleteState: deleteMutation.mutate,

    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,

    createError: createMutation.error,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,

    refreshStates,
    clearCache,
  };
};
